/* global google */
import { useEffect, useRef } from 'react';

import { useMap } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';

import { colors } from '#styles/colors';

/**
 * A marker component that works with MarkerClusterer
 * This creates a native Google Maps marker for clustering
 */
export default function ClusteredMarker({ position, onClick, isSelected }) {
	const map = useMap();
	const markerRef = useRef(null);

	useEffect(() => {
		if (!map || !position) return;

		// Create native Google Maps marker
		const marker = new google.maps.Marker({
			position,
			map,
			icon: {
				path: google.maps.SymbolPath.CIRCLE,
				fillColor: isSelected ? colors.primary[500] : colors.bgBase[600],
				fillOpacity: 1,
				strokeColor: isSelected ? colors.primary[600] : colors.bgBase[700],
				strokeWeight: 3,
				scale: 10,
			},
			clickable: true,
		});

		// Add click listener
		if (onClick) {
			marker.addListener('click', onClick);
		}

		markerRef.current = marker;

		// Cleanup
		return () => {
			if (markerRef.current) {
				google.maps.event.clearInstanceListeners(markerRef.current);
				markerRef.current.setMap(null);
				markerRef.current = null;
			}
		};
	}, [map, position, onClick, isSelected]);

	// Update marker style when selection changes
	useEffect(() => {
		if (markerRef.current) {
			markerRef.current.setIcon({
				path: google.maps.SymbolPath.CIRCLE,
				fillColor: isSelected ? colors.primary[500] : colors.bgBase[600],
				fillOpacity: 1,
				strokeColor: isSelected ? colors.primary[600] : colors.bgBase[700],
				strokeWeight: 3,
				scale: 10,
			});
		}
	}, [isSelected]);

	return null;
}

ClusteredMarker.propTypes = {
	position: PropTypes.shape({
		lat: PropTypes.number.isRequired,
		lng: PropTypes.number.isRequired,
	}).isRequired,
	onClick: PropTypes.func,
	isSelected: PropTypes.bool,
};
